// frontend/src/components/FinanceReport/Accounting/components/accounts/suppliers/SupplierMovementFormModal.jsx

import { useEffect, useMemo, useState } from "react";
import {
  createSupplierPrepayment,
  patchSupplierPrepayment,
  uploadSupplierPrepaymentDocument,
  deleteSupplierPrepaymentDocument,
  getAllOrders,
  getAllAllocations,
  getAllCurrencies,
} from "./requests";

const TYPE_OPTIONS = [
  { value: "income", label: "Приход" },
  { value: "expense", label: "Расход" },
];

const STATUS_OPTIONS = [
  { value: "PENDING", label: "Ожидает" },
  { value: "PAID", label: "Оплачено" },
];

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp";
const today = () => new Date().toISOString().slice(0, 10);

const emptyForm = () => ({
  type: "income",
  order: "",
  allocation: "",
  amount: "",
  currency: "",
  date: today(),
  payment_agent: "",
  payment_status: "PENDING",
  comment: "",
});

const getOrderCode = (o) =>
  o?.order_code || o?.code || o?.order_number || o?.number || `#${o?.id}`;

const getOrderClientName = (o) =>
  o?.client_data || o?.client_name || o?.client?.name || "";

export default function SupplierMovementFormModal({
  open,
  mode,
  initial,
  supplierId,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState(emptyForm());
  const [orders, setOrders] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [files, setFiles] = useState([]);
  const [documentsToDelete, setDocumentsToDelete] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [refLoading, setRefLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    setRefLoading(true);
    Promise.all([
      getAllOrders().catch(() => []),
      getAllAllocations().catch(() => []),
      getAllCurrencies().catch(() => []),
    ])
      .then(([o, a, c]) => {
        setOrders(o);
        setAllocations(a);
        setCurrencies(c);
      })
      .finally(() => setRefLoading(false));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setError("");
    setFiles([]);
    setDocumentsToDelete([]);

    if (mode === "edit" && initial) {
      setForm({
        type: initial.type || "income",
        order: initial.order ? String(initial.order) : "",
        allocation: initial.allocation ? String(initial.allocation) : "",
        amount: String(initial.amount ?? ""),
        currency: initial.currency ? String(initial.currency) : "",
        date: initial.date || today(),
        payment_agent: initial.payment_agent || "",
        payment_status: initial.payment_status || "PENDING",
        comment: initial.comment || "",
      });
    } else {
      const cny = currencies.find((c) => c.code === "CNY");
      setForm({ ...emptyForm(), currency: cny ? String(cny.id) : "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, initial, currencies]);

  const setField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const existingDocuments = initial?.documents || [];
  const visibleDocuments = existingDocuments.filter(
    (d) => !documentsToDelete.includes(d.id)
  );

  const handleAddFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length === 0) return;
    setFiles((prev) => {
      const keys = new Set(prev.map((f) => `${f.name}::${f.size}`));
      return [...prev, ...picked.filter((f) => !keys.has(`${f.name}::${f.size}`))];
    });
    e.target.value = "";
  };

  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));
  const markForDeletion = (id) =>
    setDocumentsToDelete((prev) => (prev.includes(id) ? prev : [...prev, id]));
  const unmarkForDeletion = (id) =>
    setDocumentsToDelete((prev) => prev.filter((x) => x !== id));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const amountNum = Number(form.amount);
    if (!amountNum || amountNum <= 0) {
      setError("Сумма должна быть больше нуля.");
      return;
    }

    if (form.type === "income" && !form.order) {
      setError("Для прихода выберите заказ.");
      return;
    }

    if (form.type === "expense" && !form.allocation) {
      setError("Для расхода выберите распределение.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        type: form.type,
        supplier: supplierId,
        amount: String(amountNum),
        currency: form.currency ? Number(form.currency) : null,
        date: form.date || null,
        payment_agent: form.payment_agent || "",
        comment: form.comment || "",
        payment_status: form.payment_status || "PENDING",
        order: form.type === "income" ? Number(form.order) : null,
        allocation: form.type === "expense" ? Number(form.allocation) : null,
      };

      let saved;
      if (mode === "edit" && initial) {
        saved = await patchSupplierPrepayment(initial.id, payload);
      } else {
        saved = await createSupplierPrepayment(payload);
      }

      const errors = [];
      for (const file of files) {
        try {
          await uploadSupplierPrepaymentDocument(saved.id, file);
        } catch (err) {
          errors.push(`Файл "${file.name}": ${err.message}`);
        }
      }
      for (const docId of documentsToDelete) {
        try {
          await deleteSupplierPrepaymentDocument(docId);
        } catch (err) {
          errors.push(`Документ #${docId}: ${err.message}`);
        }
      }

      await onSaved({ warning: errors.length > 0 ? errors.join("\n") : null });
    } catch (err) {
      setError(err.message || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  const orderOptions = useMemo(
    () =>
      orders.map((o) => ({
        value: String(o.id),
        label: `${getOrderCode(o)}${
          getOrderClientName(o) ? ` — ${getOrderClientName(o)}` : ""
        }`,
      })),
    [orders]
  );

  const allocationOptions = useMemo(
    () =>
      allocations.map((a) => ({
        value: String(a.id),
        label: `#${a.id} · ${a.order_code || `Order ${a.order}`} · ¥${Number(
          a.amount || 0
        ).toLocaleString("ru-RU", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      })),
    [allocations]
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="my-8 w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">
            {mode === "edit" && initial
              ? `Редактировать платёж #${initial.id}`
              : "Новый платёж"}
          </h2>
          <button
            type="button"
            onClick={() => !saving && onClose()}
            className="rounded-lg px-2 py-1 text-slate-500 hover:bg-slate-100"
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="mb-4 whitespace-pre-wrap rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">
              🔀 Тип операции
            </label>
            <select
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              value={form.type}
              onChange={(e) => {
                const t = e.target.value;
                setField("type", t);
                setField("order", t === "income" ? form.order : "");
                setField("allocation", t === "expense" ? form.allocation : "");
              }}
            >
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {form.type === "income" && (
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                📦 Заказ <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                value={form.order}
                onChange={(e) => setField("order", e.target.value)}
                required
                disabled={refLoading}
              >
                <option value="">
                  {refLoading ? "— загрузка заказов —" : "— выберите заказ —"}
                </option>
                {orderOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {form.type === "expense" && (
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                📋 Клиентское назначение <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                value={form.allocation}
                onChange={(e) => setField("allocation", e.target.value)}
                required
                disabled={refLoading}
              >
                <option value="">
                  {refLoading ? "— загрузка —" : "— выберите распределение —"}
                </option>
                {allocationOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                💵 Сумма
              </label>
              <input
                type="number"
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                value={form.amount}
                onChange={(e) => setField("amount", e.target.value)}
                min="0.01"
                step="0.01"
                required
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                💱 Валюта
              </label>
              <select
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                value={form.currency}
                onChange={(e) => setField("currency", e.target.value)}
              >
                <option value="">—</option>
                {currencies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.symbol} {c.code}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                📅 Дата
              </label>
              <input
                type="date"
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                value={form.date}
                onChange={(e) => setField("date", e.target.value)}
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                🚦 Статус
              </label>
              <select
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                value={form.payment_status}
                onChange={(e) => setField("payment_status", e.target.value)}
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">
              🔀 Платёж через
            </label>
            <input
              type="text"
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              value={form.payment_agent}
              onChange={(e) => setField("payment_agent", e.target.value)}
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">
              💬 Комментарий
            </label>
            <textarea
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
              rows={2}
              value={form.comment}
              onChange={(e) => setField("comment", e.target.value)}
            />
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="mb-2 text-xs font-semibold text-slate-700">
              📎 Документы
            </p>

            {visibleDocuments.map((doc) => (
              <div
                key={doc.id}
                className="mb-1 flex items-center justify-between gap-2 rounded-lg bg-white px-2 py-1 text-xs"
              >
                <a
                  href={doc.file_url || doc.file}
                  target="_blank"
                  rel="noreferrer"
                  className="truncate text-blue-700 hover:underline"
                >
                  📄 {doc.original_name || "Документ"}
                </a>
                <button
                  type="button"
                  onClick={() => markForDeletion(doc.id)}
                  className="shrink-0 rounded px-1.5 py-0.5 text-red-600 hover:bg-red-50"
                >
                  🗑
                </button>
              </div>
            ))}

            {documentsToDelete.map((id) => {
              const doc = existingDocuments.find((d) => d.id === id);
              return (
                <div
                  key={id}
                  className="mb-1 flex items-center justify-between gap-2 rounded-lg bg-red-50 px-2 py-1 text-xs"
                >
                  <span className="truncate text-red-700 line-through">
                    📄 {doc?.original_name || `#${id}`}
                  </span>
                  <button
                    type="button"
                    onClick={() => unmarkForDeletion(id)}
                    className="shrink-0 rounded px-1.5 py-0.5 text-red-700 hover:bg-white"
                  >
                    ↩
                  </button>
                </div>
              );
            })}

            {files.map((file, i) => (
              <div
                key={`${file.name}-${i}`}
                className="mb-1 flex items-center justify-between gap-2 rounded-lg bg-white px-2 py-1 text-xs"
              >
                <span className="truncate text-slate-700">⬆ {file.name}</span>
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="shrink-0 rounded px-1.5 py-0.5 text-slate-500 hover:bg-slate-100"
                >
                  ✕
                </button>
              </div>
            ))}

            <label className="mt-1 inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
              ⬆ Добавить файлы
              <input
                type="file"
                multiple
                accept={ACCEPT}
                onChange={handleAddFiles}
                className="hidden"
              />
            </label>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => !saving && onClose()}
            disabled={saving}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Отмена
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-black text-white px-5 py-2 text-sm font-semibold hover:bg-gray-800 disabled:opacity-50"
          >
            {saving ? "Сохранение…" : "Сохранить"}
          </button>
        </div>
      </form>
    </div>
  );
}